import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Mail, Inbox, MailOpen, Trash2, LogOut, ExternalLink, Loader2 } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([api.get("/admin/messages"), api.get("/admin/stats")]);
      setMessages(m.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const open = async (msg) => {
    setSelected(msg);
    if (!msg.read) {
      try {
        await api.patch(`/admin/messages/${msg.id}/read`);
        setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, read: true } : m)));
        setStats((s) => ({ ...s, unread: Math.max(0, s.unread - 1) }));
      } catch (_) {}
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Message deleted");
      load();
    } catch (_) {
      toast.error("Could not delete message");
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (user === null || (user && loading)) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="animate-spin text-accent" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-surface sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-display text-xl tracking-[0.2em]">LEYLAK<span className="text-accent">.</span></span>
            <span className="hidden sm:inline text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Messages
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-muted-foreground hover:text-accent inline-flex items-center gap-1.5 transition-colors">
              <ExternalLink size={14} /> View site
            </Link>
            <button
              data-testid="admin-logout"
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              className="text-sm inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 hover:border-accent hover:text-accent transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-2">Contact messages</h1>
        <p className="text-muted-foreground mb-10">Enquiries submitted through the website.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          <StatCard icon={Inbox} label="Total messages" value={stats.total} testid="stat-total" />
          <StatCard icon={Mail} label="Unread" value={stats.unread} testid="stat-unread" accent />
          <StatCard icon={MailOpen} label="Read" value={stats.total - stats.unread} testid="stat-read" />
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 border border-white/10 rounded-sm bg-surface overflow-hidden">
            {messages.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground text-sm">No messages yet.</div>
            ) : (
              <ul className="divide-y divide-white/10 max-h-[600px] overflow-y-auto">
                {messages.map((m) => (
                  <li key={m.id}>
                    <button
                      data-testid={`message-row-${m.id}`}
                      onClick={() => open(m)}
                      className={`w-full text-left px-5 py-4 hover:bg-white/[0.03] transition-colors ${
                        selected?.id === m.id ? "bg-white/[0.05]" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate flex items-center gap-2">
                          {!m.read && <span className="h-2 w-2 rounded-full bg-accent shrink-0" />}
                          {m.name}
                        </span>
                        <span className="text-[11px] text-muted-foreground shrink-0">{fmt(m.created_at)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate mt-1">{m.email}</div>
                      <div className="text-sm text-muted-foreground/80 truncate mt-1">{m.message}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="lg:col-span-3 border border-white/10 rounded-sm bg-surface p-6 md:p-8 min-h-[300px]">
            {selected ? (
              <div data-testid="message-detail">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl">{selected.name}</h2>
                    <a href={`mailto:${selected.email}`} className="text-accent text-sm">{selected.email}</a>
                  </div>
                  <button
                    data-testid={`delete-${selected.id}`}
                    onClick={() => remove(selected.id)}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 size={15} /> Delete
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                  <Field label="Company" value={selected.company || "—"} />
                  <Field label="Service" value={selected.service || "—"} />
                  <Field label="Received" value={fmt(selected.created_at)} />
                  <Field label="Status" value={selected.read ? "Read" : "Unread"} />
                </div>
                <div className="border-t border-white/10 pt-6">
                  <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Message</div>
                  <p className="leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                </div>
              </div>
            ) : (
              <div className="h-full grid place-items-center text-muted-foreground text-sm">
                Select a message to read.
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, accent }) {
  return (
    <div data-testid={testid} className="border border-white/10 rounded-sm bg-surface p-6 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{label}</div>
        <div className={`font-display text-4xl mt-2 ${accent ? "text-accent" : ""}`}>{value}</div>
      </div>
      <Icon size={28} className={accent ? "text-accent" : "text-muted-foreground"} strokeWidth={1.4} />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{label}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}
